<script setup lang="ts">
import {useSystemStore} from "@/stores/system.store";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {DotLottieVue} from "@lottiefiles/dotlottie-vue";
import {ref} from "vue";
import {contentfulService, healthcenterApi} from "@/container";
import {useRouter} from "vue-router";
import {Dialog, DialogContent, DialogTitle} from "@/core/components/ui/dialog";
import contentfulHelper from "@/core/helpers/contentfulHelper";
import RichTextRenderer from 'contentful-rich-text-vue-renderer';
import useSwr from "@/core/composables/swr/useSwr";
import {MessageCircle, CalendarDays, Wrench} from "@lucide/vue";
import {storeToRefs} from "pinia";
import useLgoSyncState from "@/core/composables/sante-secure/useLgoSyncState";

const {lastSyncDate, isSyncStale} = useLgoSyncState({refreshOnMount: true, watchWindowFocus: true});
const {environment} = storeToRefs(useSystemStore());
const router = useRouter();
const open = ref(false);

const {data: guides} = useSwr(
    'contentful/apodis-secure-help-guide',
    () => contentfulService().getSpecificTagGuide('apodis-secure-help'),
    contentfulService().swrConfig()
);

const {data: log} = useSwr(
    'healthcenters/' + environment.value?.entityId + '/log',
    () => environment.value?.entityId ? healthcenterApi().getLog(environment.value.entityId) : Promise.resolve(null),
);

/**
 * Open repair guide.
 */
const openGuide = () => {
    if (!guides.value?.length) {
        router.push({name: 'support'});
    } else {
        open.value = true;
    }
};

/**
 * Open hubspot chat widget.
 */
const openChat = () => {
    window.HubSpotConversations?.widget.open();
};

/**
 * Open appointment booking calendar.
 */
const openAppointment = () => {
    window.open("https://meetings.hubspot.com/alexis-perret/reparation-secure-synchronisation-donnees");
};
</script>

<template>
  <div v-if="isSyncStale" class="relative border-orange border-2 p-4 mb-8 bg-white rounded-lg">
    <div class="flex flex-row justify-between">
      <div class="flex flex-col gap-1 sm:pb-4">
        <h2 class="text-black">Vérifiez votre installation</h2>
        <p>
          Apodis Secure <span v-if="log?.host">(installé sur le poste {{ log.host }})</span> ne synchronise plus vos
          données depuis le {{ lastSyncDate }}.
          Cela peut affecter la détection des patients et l'efficacité des modules.
        </p>
        <p class="pt-4">Contactez le service client pour réparer votre installation.</p>
      </div>
      <DotLottieVue
        src="animations/no-recent-sync.lottie"
        autoplay
        loop
        class="w-64 h-32"
      />
    </div>
    <div class="absolute -bottom-6 right-0 p-2 flex flex-row items-center gap-2">
      <BaseButton primary @click="openChat">
        <MessageCircle class="size-4 mr-1.5" />
        Contacter le support
      </BaseButton>
      <BaseButton primary @click="openAppointment">
        <CalendarDays class="size-4 mr-1.5" />
        Prendre rendez-vous
      </BaseButton>
      <BaseButton primary @click="openGuide">
        <Wrench class="size-4 mr-1.5" />
        Voir le guide de réparation
      </BaseButton>
    </div>
    <Dialog v-model:open="open">
      <DialogContent class="max-h-[80%] min-w-[80%] overflow-y-auto">
        <DialogTitle>
          Guide de dépannage
        </DialogTitle>
        <RichTextRenderer
          v-if="guides.length"
          :document="guides[0].fields.content"
          :node-renderers="contentfulHelper.renderNodes()"
        />
      </DialogContent>
    </Dialog>
  </div>
</template>

<style scoped>

</style>
